// Live-status dot in the retailer topbar.
//
// Subscribes to the realtime connection state and renders a single
// coloured dot with a tooltip describing the current state. Sits in the
// topbar's right cluster between notifications and other icons (see
// header.ts for placement).
//
// Visual contract:
//   live              → small green dot, gentle pulse
//   connecting / reconnecting → amber, no pulse
//   polling-fallback  → grey, no pulse
//   offline           → red, no pulse (rare — only when realtime.stop()
//                       has been called explicitly)
//
// The dot is decorative, but the title attribute spells out the state
// so power users (and screen readers) can see exactly what's happening.

import { onStateChange, type RealtimeState } from "./realtime";

interface IndicatorHandle {
  el: HTMLElement;
  destroy: () => void;
}

const STATE_LABEL: Record<RealtimeState, string> = {
  connecting: "Connecting…",
  live: "Live — receiving updates instantly",
  reconnecting: "Reconnecting…",
  "polling-fallback": "Background polling — live stream unavailable",
  offline: "Offline",
};

export function createLiveIndicator(): IndicatorHandle {
  const el = document.createElement("span");
  el.className = "tkos-live-indicator";
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");
  el.dataset["state"] = "offline";
  el.innerHTML = `
    <span class="tkos-live-indicator__dot" aria-hidden="true"></span>
    <span class="tkos-live-indicator__sr-only"></span>
  `;
  const sr = el.querySelector<HTMLElement>(".tkos-live-indicator__sr-only");

  const off = onStateChange((state) => {
    el.dataset["state"] = state;
    el.title = STATE_LABEL[state];
    if (sr) sr.textContent = STATE_LABEL[state];
  });

  return {
    el,
    destroy: () => off(),
  };
}
